import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { MetricCard } from "../components/MetricCard";
import { DonutChart } from "../components/DonutChart";
import { BarChart } from "../components/BarChart";
import { DataSourceIndicator } from "../components/DataSourceIndicator";
import { DashboardData } from "../types";
import { fetchRetailerData } from "../api/dashboardApi";
import { generateBrandColors } from "../config/colors";

// Retailer branding configuration
const retailerConfig: Record<
  string,
  { name: string; color: string; bgColor: string }
> = {
  walmart: {
    name: "Walmart",
    color: "#004c91",
    bgColor: "bg-blue-50 dark:bg-blue-900/10",
  },
  albertsons: {
    name: "Albertsons",
    color: "#ff9900",
    bgColor: "bg-orange-50 dark:bg-orange-900/10",
  },
  kroger: {
    name: "Kroger",
    color: "#004990",
    bgColor: "bg-blue-50 dark:bg-blue-900/10",
  },
  instacart: {
    name: "Instacart",
    color: "#43b02a",
    bgColor: "bg-green-50 dark:bg-green-900/10",
  },
};

/**
 * RetailerPage displays individual retailer dashboard using existing dashboard components.
 * Features retailer-specific branding and data while reusing all existing chart and metric components.
 */
export const RetailerPage: React.FC = () => {
  const { retailerId } = useParams<{ retailerId: string }>();
  const location = useLocation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState<"api" | "mock">("mock");

  // Extract retailer ID from URL path (e.g., "/walmart" -> "walmart")
  const pathRetailerId = location.pathname.substring(1); // Remove leading slash

  // Use path-based retailer ID first, then URL param, then default to walmart
  const validRetailers = ["walmart", "albertsons", "kroger", "instacart"];
  const currentRetailerId =
    pathRetailerId && validRetailers.includes(pathRetailerId)
      ? pathRetailerId
      : retailerId && validRetailers.includes(retailerId)
        ? retailerId
        : "walmart";
  const config = retailerConfig[currentRetailerId];

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // Fetch retailer-specific data from API
        const apiData = await fetchRetailerData(currentRetailerId);
        setDataSource("api");
        setData(apiData);
      } catch (error) {
        console.error("Failed to load retailer data:", error);
        setDataSource("api");
        setData(null); // Set to null to show error state
      }
      setLoading(false);
    }
    loadData();
  }, [currentRetailerId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 text-lg">Loading retailer data…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-2">
            Failed to load retailer data
          </p>
          <p className="text-gray-500">
            Please check your API connection and try again.
          </p>
        </div>
      </div>
    );
  }

  const {
    summary,
    units_by_brand,
    units_by_customer,
    roas_by_brand,
    spend_by_day_by_brand,
    sales_by_day_by_brand,
  } = data;

  // Prepare data for Units by Customer chart (stacked vertical bar)
  const unitsCustomerLabels = units_by_customer.map((item) => item.brand_name);
  const ntbData = units_by_customer.map((item) => item.new_to_brand_units);
  const repeatData = units_by_customer.map((item) => item.repeat_units);

  // Data for ROAS horizontal bar chart
  const roasLabels = roas_by_brand.map((item) => item.brand_name);
  const roasValues = roas_by_brand.map((item) => item.roas);

  // Data for Spend by Day and Sales by Day (stacked bar)
  const spendLabels = spend_by_day_by_brand.map((item) =>
    new Date(item.date).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    }),
  );
  const salesLabels = sales_by_day_by_brand.map((item) =>
    new Date(item.date).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    }),
  );

  // Collect unique brand names from the daily values (with safety check)
  const brandNames =
    spend_by_day_by_brand.length > 0
      ? Object.keys(spend_by_day_by_brand[0].values)
      : [];

  // Generate brand colors using centralized configuration
  const BRAND_COLORS = generateBrandColors(brandNames);

  // Construct datasets for spend and sales stacked bar charts
  const spendDatasets = brandNames.map((brand) => ({
    label: brand,
    data: spend_by_day_by_brand.map((day) => day.values[brand] ?? 0),
    backgroundColor: BRAND_COLORS[brand] ?? "#3b82f6",
  }));
  const salesDatasets = brandNames.map((brand) => ({
    label: brand,
    data: sales_by_day_by_brand.map((day) => day.values[brand] ?? 0),
    backgroundColor: BRAND_COLORS[brand] ?? "#3b82f6",
  }));

  return (
    <div
      className={`min-h-screen p-4 md:p-8 space-y-6 ${config.bgColor}`}
      data-dashboard
    >
      {/* Retailer Header with Data Source Indicator */}
      <div className="flex justify-between items-center py-4">
        <div className="flex-1 text-center">
          <div
            className="inline-block px-6 py-2 rounded-full text-white font-semibold text-lg"
            style={{ backgroundColor: config.color }}
          >
            {config.name} Dashboard
          </div>
        </div>
        <DataSourceIndicator source={dataSource} />
      </div>

      {/* Metrics row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Sales"
          value={`$${(summary.sales / 1000).toFixed(0)}K`}
        />
        <MetricCard
          label="Units"
          value={`${(summary.units / 1000).toFixed(0)}K`}
        />
        <MetricCard
          label="Spend"
          value={`$${(summary.spend / 1000).toFixed(0)}K`}
        />
        <MetricCard label="ROAS" value={summary.roas.toFixed(2)} />
      </section>

      {/* Second row: ROAS, Units by Brand, Units by Customer */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Return on Ad Spend by Brand */}
        <div>
          <BarChart
            labels={roasLabels}
            datasets={[
              {
                label: "ROAS",
                data: roasValues,
                backgroundColor: config.color,
              },
            ]}
            title="Return on Ad Spend by Brand"
            stacked={false}
            horizontal={true}
            showLegend={false}
          />
        </div>
        {/* Units by Brand (Donut) */}
        <div>
          <DonutChart data={units_by_brand} title="Units by Brand" />
        </div>
        {/* Units by Customer (Stacked Bar) */}
        <div>
          <BarChart
            labels={unitsCustomerLabels}
            datasets={[
              {
                label: "New to Brand",
                data: ntbData,
                backgroundColor: "#60a5fa",
              },
              {
                label: "Repeat",
                data: repeatData,
                backgroundColor: config.color,
              },
            ]}
            title="Units by Customer"
            stacked={true}
            horizontal={false}
            showLegend={true}
          />
        </div>
      </section>

      {/* Third row: Spend and Sales by Day by Brand */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Spend by Day by Brand */}
        <div>
          <BarChart
            labels={spendLabels}
            datasets={spendDatasets}
            title="Spend by Day by Brand"
            stacked={true}
            horizontal={false}
            showLegend={true}
          />
        </div>
        {/* Sales by Day by Brand */}
        <div>
          <BarChart
            labels={salesLabels}
            datasets={salesDatasets}
            title="Sales by Day by Brand"
            stacked={true}
            horizontal={false}
            showLegend={true}
          />
        </div>
      </section>
    </div>
  );
};
